/* LAV TDMS — authentication: login + project selector. */
import { useState } from 'react'
import type { Project } from '@/data/types'
import { qk, useMeta } from '@/data/queries'
import { refreshSession } from '@/auth/oidc'
import { useCommand } from '@/data/mutations'
import { useMe } from '@/data/meta'
import { Input, Button, Avatar, Dialog, Field, Spinner, toast } from '@/ui'
import { Icon } from '@/ds/icons'
import { LANGS, t, type Lang } from '@/i18n/lang'

type Theme = 'light' | 'dark'


function Brandmark({ size = 26 }: { size?: number }) {
  return (
    <div style={{ width: size, height: size, flex: '0 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: size * 0.1, padding: size * 0.2, background: 'var(--amber-400)', borderRadius: 1 }}>
      <span style={{ height: size * 0.1, width: '100%', background: 'var(--slate-900)' }} />
      <span style={{ height: size * 0.1, width: '100%', background: 'rgba(28,37,48,.55)' }} />
      <span style={{ height: size * 0.1, width: '62%', background: 'rgba(28,37,48,.55)' }} />
    </div>
  )
}

interface LoginProps {
  lang: Lang
  onLang: (l: Lang) => void
  theme: Theme
  onTheme: (theme: Theme) => void
  /** Уводит на страницу входа Keycloak. Учётные данные в это приложение не попадают. */
  onSignIn: () => void
  error?: string | null
}

export function Login({ lang, onLang, theme, onTheme, onSignIn, error }: LoginProps) {
  const vocab: [string, string][] = [['MDR', 'Реестр документов'], ['VDR', 'Реестр поставщика'], ['TRM', 'Трансмиттал'], ['CRS / ACSR', 'Замечания и ответы']]
  return (
    <div data-theme={theme} style={{ minHeight: '100vh', background: 'var(--surface-page)', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 880, display: 'grid', gridTemplateColumns: '1.05fr 1fr', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
        {/* Identity panel */}
        <div style={{ background: 'var(--slate-900)', padding: '34px 32px', display: 'flex', flexDirection: 'column', gap: 22, position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <Brandmark size={34} />
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
              <span style={{ fontWeight: 700, fontSize: 19, letterSpacing: '.06em', color: 'var(--slate-100)' }}>LAV</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 14, color: 'var(--amber-300)', letterSpacing: '.1em' }}>TDMS</span>
            </div>
          </div>
          <div>
            <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--amber-300)', marginBottom: 10 }}>Technical Document Management</div>
            <h1 style={{ font: 'var(--weight-semibold) 25px/1.25 var(--font-sans)', color: 'var(--white)', letterSpacing: '-.01em' }}>{t(lang, 'Приёмка и рассмотрение документации EPC-подрядчика', 'Receipt & review of EPC contractor documentation')}</h1>
            <p style={{ marginTop: 12, font: 'var(--font-ui)', fontSize: 13.5, color: 'var(--slate-400)', lineHeight: 1.6 }}>{t(lang, 'Единая платформа документооборота стороны Заказчика: трансмитталы, реестры MDR/VDR, замечания и контроль сроков.', "The owner's single document-control platform: transmittals, MDR/VDR registers, comments and schedule control.")}</p>
          </div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 7 }}>
            {vocab.map(([k, v]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--amber-300)', background: 'rgba(232,154,44,.12)', border: '1px solid rgba(232,154,44,.3)', borderRadius: 2, padding: '2px 7px', minWidth: 74, textAlign: 'center' }}>{k}</span>
                <span style={{ font: 'var(--font-ui)', fontSize: 12.5, color: 'var(--slate-300)' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Form */}
        <div style={{ padding: '34px 32px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 22 }}>
            {/* hdr-btn переиспользуется из шапки ради press и transition, но его
                hover-фон — белая плёнка под тёмный header; на светлой карточке
                входа он невидим, поэтому цвет hover переопределён локальным
                правилом .tdms-authbtn ниже. Активному языку класс не даётся:
                hover перекрасил бы акцентную заливку выбранного. */}
            <div style={{ display: 'flex', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginLeft: 'auto' }}>
              {LANGS.map((l) => <button key={l} onClick={() => onLang(l)} className={lang === l ? undefined : 'hdr-btn tdms-authbtn'} style={{ width: 34, height: 26, border: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', background: lang === l ? 'var(--accent)' : 'transparent', color: lang === l ? 'var(--on-accent)' : 'var(--text-subtle)' }}>{l}</button>)}
            </div>
            <button onClick={() => onTheme(theme === 'dark' ? 'light' : 'dark')} className="hdr-btn tdms-authbtn" style={{ width: 28, height: 28, marginLeft: 8, display: 'grid', placeItems: 'center', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' }}><Icon name={theme === 'dark' ? 'sun' : 'moon'} size={14} /></button>
          </div>
          <h2 style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)', marginBottom: 4 }}>{t(lang, 'Вход в систему', 'Sign in')}</h2>
          <p style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', marginBottom: 22 }}>{t(lang, 'Вход выполняется на сервере единой учётной записи. Пароль в это приложение не передаётся.', 'Sign-in happens on the identity server. Your password never reaches this application.')}</p>
          {error ? (
            <div role="alert" style={{ marginBottom: 16, padding: '10px 12px', border: '1px solid var(--danger-line, var(--border-strong))', borderRadius: 'var(--radius-sm)', background: 'var(--danger-soft, transparent)', color: 'var(--danger-ink, var(--text-body))', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>{error}</div>
          ) : null}
          <form onSubmit={(e) => { e.preventDefault(); onSignIn() }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Button variant="primary" size="lg" block type="submit" iconRight={<Icon name="arrow-right" size={15} />}>{t(lang, 'Войти', 'Sign in')}</Button>
          </form>
          <div style={{ marginTop: 'auto', paddingTop: 22, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            <Icon name="shield-check" size={14} />{t(lang, 'Защищённое соединение · журнал аудита всех действий', 'Secure connection · full audit trail')}
          </div>
        </div>
      </div>
      {/* Перекраска hover для hdr-btn. Базовое правило ui.css —
          .hdr-btn:not(:disabled):hover, это 0,3,0 с !important; двух классов
          здесь мало (тоже 0,3,0), и победителя решал бы порядок листов в
          документе — хрупко. :not(:disabled) поднимает override до 0,4,0:
          побеждает специфичность. */}
      <style>{`
        @media (hover: hover) and (pointer: fine) {
          .hdr-btn.tdms-authbtn:not(:disabled):hover { background: var(--surface-hover) !important; }
        }
      `}</style>
    </div>
  )
}

interface ProjectSelectProps {
  lang: Lang
  onOpen: (p: Project) => void
  onBack?: () => void
  theme: Theme
  /**
   * Проекты, разрешённые ТОКЕНОМ. Список карточек шире: он по членству в БД,
   * и проект, куда назначили после входа, в токен ещё не попал. Такая карточка
   * помечается, а не открывается — молчаливый откат на первый разрешённый
   * читался как «слетает на демо».
   */
  allowed: string[]
}

export function ProjectSelect({ lang, onOpen, onBack, theme, allowed }: ProjectSelectProps) {
  /* Список проектов и личность — с сервера. Показатели чужого проекта тоже
     сведения, поэтому сервер отдаёт только доступные вошедшему. */
  const projectsQuery = useMeta<Project[]>('projects')
  const projects = projectsQuery.data ?? []
  const me = useMe().data
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ id: '', name: '', plant: '' })
  const setF = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))
  /* Выпуск проекта — серверная команда: заводится не строка, а рабочее место —
     роли с правами, справочники, маски, маршрут и учётка создателя (он
     становится администратором проекта). Право — realm-роль admin в Keycloak;
     без неё сервер ответит 403, и это честнее спрятанной кнопки: право живёт
     вне проекта, и клиент его не знает. */
  const create = useCommand<{ id: string; name: string; plant: string }, { id: string; access?: string }>({
    path: () => '/projects',
    body: (a) => a,
    invalidate: () => [qk.meta('projects')],
  })
  const ready = form.id.trim().length >= 2 && form.name.trim() !== '' && form.plant.trim() !== ''
  const submit = () => create.mutate(
    { id: form.id.trim(), name: form.name.trim(), plant: form.plant.trim() },
    {
      onSuccess: (r) => {
        setCreating(false)
        setForm({ id: '', name: '', plant: '' })
        if (r.access === 'granted') {
          /* Сервер дописал проект в атрибут создателя — но в ТЕКУЩЕМ токене
             его ещё нет. Обновление токена обязано случиться до входа, иначе
             интерфейс откатит выбор на первый разрешённый проект. Не удалось
             обновить — честная инструкция перевойти, а не молчаливый откат. */
          void refreshSession()
            .then(() => toast({
              tone: 'success',
              title: t(lang, `Проект ${r.id} создан`, `Project ${r.id} created`),
              text: t(lang, 'Доступ выдан — проект можно открыть.', 'Access granted — the project is ready to open.'),
            }))
            .catch(() => toast({
              tone: 'info',
              title: t(lang, `Проект ${r.id} создан`, `Project ${r.id} created`),
              text: t(lang,
                'Доступ выдан, но токен не обновился. Выйдите и войдите заново.',
                'Access granted, but the token did not refresh. Sign out and back in.'),
            }))
          return
        }
        /* Интеграция с Keycloak выключена: атрибут project правит администратор
           Keycloak (решение заказчика 17.08.26). Промолчать здесь значит
           получить «создал, а войти не могу». */
        toast({
          tone: 'success',
          title: t(lang, `Проект ${r.id} создан`, `Project ${r.id} created`),
          text: t(lang,
            'Чтобы войти в него, администратор Keycloak должен указать проект в атрибуте project учётной записи.',
            'To enter it, the Keycloak administrator must set the project attribute of the account.'),
        })
      },
    },
  )
  return (
    <div data-theme={theme} style={{ minHeight: '100vh', background: 'var(--surface-page)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ height: 'var(--topbar-h)', display: 'flex', alignItems: 'center', gap: 11, padding: '0 18px', background: 'var(--surface-header)', borderBottom: '1px solid var(--border-header)' }}>
        <Brandmark />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}><span style={{ fontWeight: 700, fontSize: 14.5, letterSpacing: '.06em', color: 'var(--text-on-header)' }}>LAV</span><span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 11.5, color: 'var(--amber-300)', letterSpacing: '.08em' }}>TDMS</span></div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ font: 'var(--font-ui)', fontSize: 12.5, color: 'var(--slate-400)' }}>{me?.user.name ?? ''}</span>
          <Avatar name={me?.user.name ?? ''} size={28} />
          {/* Выход отсюда был объявлен пропом и не принят: из выбора проекта
              не было выхода вообще, только перезагрузка вкладки. */}
          {onBack ? (
            <Button variant="dark" size="sm" iconLeft={<Icon name="log-out" size={13} />} onClick={onBack}>{t(lang, 'Выйти', 'Sign out')}</Button>
          ) : null}
        </div>
      </header>
      <div style={{ flex: 1, overflow: 'auto', padding: '40px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--accent-ink)', marginBottom: 8 }}>{t(lang, 'Шаг 1 · Выбор проекта', 'Step 1 · Select a project')}</div>
          <h1 style={{ font: 'var(--font-display)', fontSize: 30, color: 'var(--text-strong)', letterSpacing: '-.02em' }}>{t(lang, 'Выберите проект', 'Select a project')}</h1>
          <p style={{ marginTop: 8, font: 'var(--font-body)', color: 'var(--text-muted)', maxWidth: 560 }}>{t(lang, 'Все документы, трансмитталы и замечания изолированы в рамках проекта. Вы видите только проекты, к которым назначены.', 'All documents, transmittals and comments are isolated per project. You only see projects you are assigned to.')}</p>
          {/* Пустая сетка читалась бы как «проектов у вас нет». Пока список не
              пришёл — так и сказано, а отказ назван отказом. */}
          {projectsQuery.isError ? (
            <div style={{ marginTop: 26, padding: '14px 16px', border: '1px solid var(--danger-line)', borderRadius: 'var(--radius-md)', background: 'var(--surface-card)' }}>
              <div style={{ font: 'var(--font-card-title)', color: 'var(--danger-ink)', marginBottom: 4 }}>{t(lang, 'Не удалось получить список проектов', 'Failed to load the project list')}</div>
              <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{t(lang, 'Сервер не ответил. Это не значит, что проектов нет.', 'The server did not answer. That does not mean there are none.')}</div>
            </div>
          ) : projectsQuery.isPending ? (
            /* Спиннер рядом с текстом: неподвижная надпись неотличима от
               зависания, вращение — единственный видимый признак запроса. */
            <div style={{ marginTop: 26, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              {/* Один компонент на все «Загрузка…»: голый span расходился бы
                  с эталоном при первой же правке .spinner. */}
              <Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}
            </div>
          ) : null}
          <div style={{ marginTop: 26, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {projects.map((p, i) => {
              /* Пустой allowed — токен без claim project (профиль demo с
                 подстановкой): помечать нечего, сервер и так пускает. */
              const locked = allowed.length > 0 && !allowed.includes(p.id)
              const open = () => {
                if (!locked) { onOpen(p); return }
                /* Назначение уже в БД, но не в токене этой сессии. Раньше клик
                   молча откатывался на первый разрешённый проект — читалось
                   как «слетает на демо». */
                toast({
                  tone: 'info',
                  title: t(lang, `Проект ${p.id} ещё не в текущей сессии`, `Project ${p.id} is not in the current session yet`),
                  text: t(lang,
                    'Доступ назначен, но токен выдан раньше. Выйдите и войдите заново.',
                    'Access is assigned, but the token predates it. Sign out and back in.'),
                })
              }
              return (
              /* Вход карточек со ступенькой 30 мс (первые ~8 — дальше хвост
                 идёт разом): парадный экран раз в сессию, единственное место
                 области, где stagger уместен. backwards держит карточку
                 невидимой, пока не подошла её задержка. Литерал .14s заменён
                 токенами — иначе переход не гасился reduced-motion. */
              <button key={p.id} onClick={open} className="tdms-proj" style={{ textAlign: 'left', padding: 0, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', background: 'var(--surface-card)', cursor: 'pointer', overflow: 'hidden', transition: 'border-color var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)', opacity: locked ? 0.72 : undefined, animation: 'tdms-proj-in var(--duration-normal) var(--ease-out) backwards', animationDelay: `${Math.min(i, 8) * 30}ms` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                  <span style={{ width: 10, height: 10, borderRadius: 999, background: `var(${p.color})`, flex: '0 0 auto' }} />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{p.id}</span>
                  {locked ? <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-subtle)', border: '1px solid var(--border-strong)', borderRadius: 2, padding: '1px 6px' }}><Icon name="lock" size={10} />{t(lang, 'НУЖЕН ПЕРЕВХОД', 'RE-LOGIN NEEDED')}</span>
                   : p.active ? <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent-ink)', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 2, padding: '1px 6px' }}>ТЕКУЩИЙ</span> : <span style={{ marginLeft: 'auto', color: 'var(--text-subtle)', display: 'inline-flex' }}><Icon name="arrow-right" size={15} /></span>}
                </div>
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{p.name}</div>
                  <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', marginTop: 2 }}>{p.plant}</div>
                  <div style={{ marginTop: 14, display: 'flex', gap: 18 }}>
                    <Stat label={t(lang, 'Документов', 'Documents')} value={p.docs} />
                    <Stat label={t(lang, 'Открытых', 'Open')} value={p.open} />
                    <Stat label={t(lang, 'Просрочено', 'Overdue')} value={p.due} danger={p.due > 0} />
                  </div>
                  <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 7, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-subtle)', display: 'inline-flex' }}><Icon name="user-round" size={13} /></span>
                    <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{t(lang, 'Ваша роль:', 'Your role:')} {p.role}</span>
                  </div>
                </div>
              </button>
              )
            })}
            <button onClick={() => setCreating(true)} className="tdms-proj" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, minHeight: 200, border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-md)', background: 'transparent', cursor: 'pointer', animation: 'tdms-proj-in var(--duration-normal) var(--ease-out) backwards', animationDelay: `${Math.min(projects.length, 8) * 30}ms` }}>
              <span style={{ display: 'grid', placeItems: 'center', width: 46, height: 46, borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', color: 'var(--accent-ink)' }}><Icon name="plus" size={24} /></span>
              <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Создать проект', 'New project')}</span>
              <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'Новый проект документооборота', 'New EDMS project')}</span>
            </button>
          </div>
        </div>
      </div>
      <Dialog open={creating} onClose={() => setCreating(false)} title={t(lang, 'Новый проект', 'New project')}>
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label={t(lang, 'Код проекта', 'Project code')}><Input value={form.id} onChange={(e) => setF('id', e.target.value.toUpperCase())} placeholder="NLMK-BF7" /></Field>
          <Field label={t(lang, 'Наименование', 'Name')}><Input value={form.name} onChange={(e) => setF('name', e.target.value)} placeholder="Доменная печь №7" /></Field>
          <Field label={t(lang, 'Предприятие', 'Plant')}><Input value={form.plant} onChange={(e) => setF('plant', e.target.value)} placeholder="НЛМК · Липецк" /></Field>
          {/* Выбора роли больше нет: создатель всегда администратор своего
              проекта — роль без права её реализовать была бы декорацией. */}
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            {t(lang,
               'Проект заводится с ролями, справочниками, масками кодирования и маршрутом рассмотрения. Создатель становится его администратором. Право создавать проекты — роль admin реалма Keycloak.',
               'The project comes with roles, dictionaries, coding masks and a review route. The creator becomes its administrator. Creating projects requires the Keycloak realm role admin.')}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <Button variant="secondary" onClick={() => setCreating(false)}>{t(lang, 'Отмена', 'Cancel')}</Button>
            <Button variant="primary" iconLeft={<Icon name="plus" size={14} />} disabled={!ready || create.isPending}
                    title={ready ? undefined : t(lang, 'Заполните код, наименование и предприятие', 'Fill in the code, name and plant')}
                    onClick={submit}>{create.isPending ? t(lang, 'Создаём…', 'Creating…') : t(lang, 'Создать проект', 'Create')}</Button>
          </div>
        </div>
      </Dialog>
      {/* Hover — под условием указателя, как у .tdms-nav/.tdms-row в Shell:
          на касании голое правило срабатывало от тапа и липло — заметнее
          всего на заблокированных карточках и «Создать проект». */}
      <style>{`
        @media (hover: hover) and (pointer: fine) {
          .tdms-proj:hover { border-color: var(--accent) !important; box-shadow: var(--shadow-sm); }
        }
        @keyframes tdms-proj-in { from { opacity: 0; transform: translateY(4px); } }
        /* Длительность гасят токены (1мс), но задержки ступеньки — нет: без
           этого правила карточки при «меньше движения» всё равно проявлялись
           бы по очереди четверть секунды. */
        @media (prefers-reduced-motion: reduce) {
          .tdms-proj { animation-delay: 0ms !important; }
        }
      `}</style>
    </div>
  )
}

function Stat({ label, value, danger }: { label: string; value: number | string; danger?: boolean }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 600, color: danger ? 'var(--danger-ink)' : 'var(--text-strong)', fontFeatureSettings: '"tnum" 1' }}>{value}</div>
      <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginTop: 2 }}>{label}</div>
    </div>
  )
}
